/**
 * Salutation values used across frontend forms and backend DTOs.
 * The SH API Gateway expects German labels (`Herr` / `Frau`); the
 * {@link salutationToGateway} helper maps the enum to those values.
 */
export enum Salutation {
  Mr = 'mr',
  Mrs = 'mrs',
  None = 'none',
}

const SALUTATION_VALUES: ReadonlySet<string> = new Set(
  Object.values(Salutation),
);

/** Type guard: is `value` one of the known {@link Salutation} values? */
export function isSalutation(value: string): value is Salutation {
  return SALUTATION_VALUES.has(value);
}

/** Maps the enum to the German labels the SH API Gateway expects. */
const GATEWAY_LABELS: Record<Salutation, string> = {
  [Salutation.Mr]: 'Herr',
  [Salutation.Mrs]: 'Frau',
  [Salutation.None]: '',
};

export function salutationToGateway(salutation: Salutation): string {
  return GATEWAY_LABELS[salutation];
}

/**
 * Maps the enum to the single letters the NHL-Tool applications endpoint
 * expects: `H` (Herr), `F` (Frau), `D` (Divers, our "Keine Angabe").
 *
 * Deliberately separate from {@link salutationToGateway} — applications do not
 * go through the SH API Gateway and none of its German labels are understood
 * here. The endpoint does **not** validate this: it accepts any non-empty
 * string with a `201` and stores it, so a wrong mapping fails silently rather
 * than erroring. That is why this mapping is unit-tested.
 */
const NHL_TOOL_LETTERS: Record<Salutation, string> = {
  [Salutation.Mr]: 'H',
  [Salutation.Mrs]: 'F',
  [Salutation.None]: 'D',
};

export function salutationToNhlTool(salutation: Salutation): string {
  return NHL_TOOL_LETTERS[salutation];
}
